import React from 'react';
import { Link } from 'react-router-dom';

const Footer: React.FC = () => {
  return (
    <footer className="bg-gray-950 border-t border-gray-800 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-5 gap-8">
          {/* Brand Section */}
          <div className="md:col-span-2 space-y-4">
            <Link to="/" className="flex items-center space-x-3 group">
              <img 
                src="/logo.png" 
                alt="BarnBills Logo" 
                className="h-12 w-auto transition-transform group-hover:scale-105"
              />
            </Link>
            <p className="text-gray-400 max-w-md leading-relaxed">
              The simple, powerful way to manage your horse farm. Built by horse people, 
              for horse people. Finally, farm management software for this century.
            </p>
            <div className="flex space-x-4">
              <a 
                href="https://x.com/barnbills" 
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Follow us on X"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </a>
              <a 
                href="https://www.linkedin.com/company/barnbills/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Follow us on LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
              <a 
                href="https://www.youtube.com/@Barn-Bills"
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 hover:text-white transition-colors"
                aria-label="Subscribe to our YouTube channel"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </a>
            </div>
          </div>
          
          {/* Product Links */}
          <div className="space-y-4">
            <h4 className="font-semibold text-white">Product</h4>
            <div className="space-y-2 text-gray-400 text-sm">
              <Link to="/features" className="block hover:text-white transition-colors">Features</Link>
              <Link to="/plugins" className="block hover:text-white transition-colors">Plugins</Link>
              <Link to="/pricing" className="block hover:text-white transition-colors">Pricing</Link>
              <Link to="/security" className="block hover:text-white transition-colors">Security</Link>
            </div>
          </div>
          
          {/* Company Links */}
          <div className="space-y-4">
            <h4 className="font-semibold text-white">Company</h4>
            <div className="space-y-2 text-gray-400 text-sm">
              <Link to="/about" className="block hover:text-white transition-colors">About Us</Link>
              <Link to="/contact" className="block hover:text-white transition-colors">Contact</Link>
              <Link to="/blog" className="block hover:text-white transition-colors">Blog</Link>
              <Link to="/careers" className="block hover:text-white transition-colors">Careers</Link>
            </div>
          </div>
          
          {/* Support & Legal Links */}
          <div className="space-y-4">
            <h4 className="font-semibold text-white">Support</h4>
            <div className="space-y-2 text-gray-400 text-sm">
              <Link to="/support" className="block hover:text-white transition-colors">Help Center</Link>
              <Link to="/contact" className="block hover:text-white transition-colors">Contact Support</Link>
              <Link to="/legal" className="block hover:text-white transition-colors">Legal</Link>
              <Link to="/terms" className="block hover:text-white transition-colors">Terms of Service</Link>
              <Link to="/privacy" className="block hover:text-white transition-colors">Privacy Policy</Link>
            </div>
          </div>
        </div>
        
        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-16 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <p className="text-gray-500 text-sm">
              &copy; 2025 BarnBills LLC. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 text-sm text-gray-500">
              <span className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span>Coming soon</span>
              </span>
              <span>Made with ❤️ for horse farms</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 